import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, Users, TrendingUp, Settings, RefreshCw,
  Eye, Trash2, Download, Filter, Search, CheckCircle,
  Clock, AlertCircle, Cpu, Brain, Bell, UserCheck,
  Lock, LogOut
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const AdminPage = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem('adminToken') || '');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [testingProvider, setTestingProvider] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [defaultProvider, setDefaultProvider] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Verifier si un token existe deja
  useEffect(() => {
    if (adminToken) {
      setAuthenticated(true);
      fetchData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword })
      });
      const data = await res.json();
      if (data.success) {
        setAdminToken(data.token);
        sessionStorage.setItem('adminToken', data.token);
        setAuthenticated(true);
        setLoginPassword('');
        fetchData(data.token);
      } else {
        setLoginError(data.error || 'Mot de passe incorrect');
      }
    } catch {
      setLoginError('Erreur de connexion au serveur');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setAdminToken('');
    setAuthenticated(false);
    sessionStorage.removeItem('adminToken');
    setStats(null);
    setSessions([]);
  };

  const authHeaders = (token) => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token || adminToken}`
  });

  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchSessions();
    }
  }, [activeTab, statusFilter, sectorFilter, currentPage]);

  const fetchData = async (token) => {
    try {
      const [statsRes, providersRes] = await Promise.all([
        fetch('/api/admin/stats', { headers: authHeaders(token) }),
        fetch('/api/ai/providers')
      ]);

      if (statsRes.status === 401) {
        handleLogout();
        return;
      }

      const statsData = await statsRes.json();
      const providersData = await providersRes.json();

      if (statsData.success) setStats(statsData.data);
      if (providersData.success) {
        setProviders(providersData.data.providers);
        setDefaultProvider(providersData.data.default);
      }

    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      let url = `/api/admin/sessions?page=${currentPage}&limit=10`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (sectorFilter) url += `&sector=${sectorFilter}`;

      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();

      if (data.success) {
        setSessions(data.data);
      }
    } catch (err) {
      console.error('Erreur sessions:', err);
    }
  };

  const testProvider = async (providerId) => {
    setTestingProvider(providerId);
    try {
      const res = await fetch('/api/ai/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: providerId })
      });
      const data = await res.json();
      
      setTestResults(prev => ({
        ...prev,
        [providerId]: {
          success: data.success,
          response: data.response,
          model: data.model,
          error: data.error
        }
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [providerId]: { success: false, error: err.message }
      }));
    } finally {
      setTestingProvider(null);
    }
  };

  const markLeadContacted = async (leadId) => {
    try {
      const res = await fetch(`/api/admin/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status: 'contacted' })
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
      }
    } catch (err) {
      console.error('Erreur mise à jour lead:', err);
    }
  };

  const COLORS = ['#00bcd4', '#0891b2', '#06b6d4', '#22d3ee', '#67e8f9'];

  // Ecran de login
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card max-w-sm w-full text-center"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Administration</h1>
          <p className="text-gray-500 text-sm mb-6">Connectez-vous pour accéder au tableau de bord</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="Mot de passe"
              className="input text-center"
              autoFocus
            />
            {loginError && (
              <p className="text-sm text-red-500">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={!loginPassword || loginLoading}
              className="btn btn-primary w-full"
            >
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loader"></div>
      </div>
    );
  }

  // Prepare chart data
  const sectorChartData = stats?.sessionsBySector?.map(s => ({
    name: s.sector_id,
    value: s.count
  })) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <span className="text-xl font-bold text-gray-900">Ydv<span className="brand-gradient">Systems</span></span>
              <span className="text-[#d1d1d6]">|</span>
              <span className="text-xl font-bold text-[#636366]">Audit IA</span>
            </button>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600 font-medium">Administration</span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              className="btn btn-ghost flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
            <button
              onClick={handleLogout}
              className="btn btn-ghost flex items-center gap-2 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'sessions', label: 'Sessions', icon: Users },
            { id: 'providers', label: 'Providers IA', icon: Cpu }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg transition-all
                ${activeTab === tab.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'}
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 text-primary-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total sessions</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.totalSessions || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 text-green-600 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Complétées</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.completedSessions || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 text-primary-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Score moyen</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {Math.round(stats?.averageMaturityScore || 0)}/100
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">En cours</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(stats?.totalSessions || 0) - (stats?.completedSessions || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Badge leads non contactés */}
            {stats?.newLeadsCount > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-red-800">
                    {stats.newLeadsCount} lead{stats.newLeadsCount > 1 ? 's' : ''} non contacté{stats.newLeadsCount > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-red-600">Des prospects attendent votre retour</p>
                </div>
              </div>
            )}

            {/* Leads récents */}
            {stats?.recentLeads?.length > 0 && (
              <div className="card mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-primary-500" />
                  Leads récents
                </h3>
                <div className="space-y-3">
                  {stats.recentLeads.map(lead => (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {lead.contact_name || '-'} — {lead.company_name || 'Entreprise inconnue'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {lead.email || '-'} {lead.phone ? `• ${lead.phone}` : ''}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(lead.created_at).toLocaleString('fr-FR')}
                          {lead.notes ? ` • ${lead.notes}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => markLeadContacted(lead.id)}
                        className="ml-4 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors flex items-center gap-1"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Contacté
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Sessions by sector */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Sessions par secteur
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={sectorChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {sectorChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Recent activity */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Activité récente
                </h3>
                <div className="space-y-3">
                  {stats?.recentSessions?.slice(0, 5).map((session, i) => (
                    <div 
                      key={session.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-2 h-2 rounded-full
                          ${session.status === 'completed' ? 'bg-green-500' : 'bg-amber-500'}
                        `} />
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {session.sector_id}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(session.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/results/${session.id}`)}
                        className="text-primary-500 hover:text-primary-700"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AI Providers Status */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Status des providers IA
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {providers.map(provider => (
                  <div 
                    key={provider.id}
                    className={`
                      p-4 rounded-lg border-2
                      ${provider.available 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-gray-200 bg-gray-50'}
                    `}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brain className={`w-5 h-5 ${provider.available ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="font-medium">{provider.name}</span>
                      </div>
                      {provider.available ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Modèle: {provider.model}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Filters */}
            <div className="card mb-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="input pl-10"
                    />
                  </div>
                </div>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="">Tous les statuts</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Complétées</option>
                </select>
                
                <select
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                  className="input w-auto"
                >
                  <option value="">Tous les secteurs</option>
                  <option value="industrie">Industrie</option>
                  <option value="commerce">Commerce</option>
                  <option value="services">Services</option>
                  <option value="sante">Santé</option>
                </select>
              </div>
            </div>

            {/* Sessions table */}
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Secteur</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Entreprise</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sessions.map(session => (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {session.id.slice(0, 8)}...
                        </code>
                      </td>
                      <td className="px-4 py-3 text-sm">{session.sector_id}</td>
                      <td className="px-4 py-3 text-sm">
                        {session.companyInfo?.company_name || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`
                          text-xs px-2 py-1 rounded-full
                          ${session.status === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'}
                        `}>
                          {session.status === 'completed' ? 'Complété' : 'En cours'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(session.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/results/${session.id}`)}
                            className="p-2 text-gray-500 hover:text-primary-500 hover:bg-primary-50 rounded-lg"
                            title="Voir les résultats"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Télécharger"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {sessions.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Aucune session trouvée
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* AI Providers Tab */}
        {activeTab === 'providers' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Provider actif */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Provider IA actif
              </h3>
              <p className="text-gray-600 mb-4">
                Le provider sélectionné sera utilisé pour toutes les analyses de diagnostic.
              </p>
              <div className="flex items-center gap-4">
                <select
                  value={defaultProvider || ''}
                  onChange={(e) => setDefaultProvider(e.target.value)}
                  className="input w-auto"
                >
                  {providers.filter(p => p.available).map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.model})</option>
                  ))}
                </select>
                <span className="text-sm text-gray-500">
                  Configurable via <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">DEFAULT_AI_PROVIDER</code> dans .env
                </span>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Configuration des providers IA
              </h3>
              <p className="text-gray-600 mb-6">
                Testez la connexion aux différents services d'IA et vérifiez leur disponibilité.
              </p>

              <div className="space-y-4">
                {providers.map(provider => (
                  <div 
                    key={provider.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className={`
                          w-12 h-12 rounded-xl flex items-center justify-center
                          ${provider.id === 'claude' ? 'bg-orange-100 text-orange-600' :
                            provider.id === 'openai' ? 'bg-green-100 text-green-600' :
                            'bg-blue-100 text-blue-600'}
                        `}>
                          <Brain className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                          <p className="text-sm text-gray-500">Modèle: {provider.model}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className={`
                          px-3 py-1 rounded-full text-sm font-medium
                          ${provider.available 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'}
                        `}>
                          {provider.available ? 'Disponible' : 'Non configuré'}
                        </span>
                        
                        {provider.available && (
                          <button
                            onClick={() => testProvider(provider.id)}
                            disabled={testingProvider === provider.id}
                            className="btn btn-secondary text-sm flex items-center gap-2"
                          >
                            {testingProvider === provider.id ? (
                              <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Test...
                              </>
                            ) : (
                              <>
                                <Cpu className="w-4 h-4" />
                                Tester
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Test results */}
                    {testResults[provider.id] && (
                      <div className={`
                        mt-4 p-3 rounded-lg text-sm
                        ${testResults[provider.id].success 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-red-50 text-red-700'}
                      `}>
                        {testResults[provider.id].success ? (
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                            <div>
                              <p className="font-medium">Connexion réussie !</p>
                              <p className="mt-1 opacity-75">
                                Réponse: "{testResults[provider.id].response}"
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <div>
                              <p className="font-medium">Erreur de connexion</p>
                              <p className="mt-1 opacity-75">
                                {testResults[provider.id].error}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Configuration info */}
                    {!provider.available && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                        <p className="font-medium mb-1">Comment configurer :</p>
                        <p>
                          Ajoutez la variable d'environnement{' '}
                          <code className="bg-gray-200 px-1 rounded">
                            {provider.id === 'claude' ? 'ANTHROPIC_API_KEY' :
                              provider.id === 'openai' ? 'OPENAI_API_KEY' :
                              'GOOGLE_AI_API_KEY'}
                          </code>
                          {' '}dans votre fichier <code className="bg-gray-200 px-1 rounded">.env</code>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Usage tips */}
            <div className="card bg-primary-50 border-primary-200">
              <h4 className="font-semibold text-primary-900 mb-2">
                💡 Conseils d'utilisation
              </h4>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• Claude est recommandé pour les analyses détaillées et nuancées</li>
                <li>• GPT-4 excelle dans la génération de rapports structurés</li>
                <li>• Gemini est performant pour les analyses multimodales</li>
                <li>• Le système bascule automatiquement vers un provider disponible en cas d'erreur</li>
              </ul>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
